
/**
 * 'waiter' takes orders at the table but never handles money, the ticket
 * stays open until a cashier settles it.
 */
export type Role = 'owner' | 'manager' | 'cashier' | 'waiter' | 'kitchen' | 'kiosk'

/**
 * What a person may do beyond what their role already allows.
 *
 * The role draws the outline, a waiter never takes money, but inside it
 * are things that differ from one business to the next: here a waiter may
 * move a customer to another table, there they may not. These are not roles,
 * they are settings within one, so they hang on the person rather than
 * multiplying the roles.
 *
 * Absent means off, like the institution's own feature flags. Only the owner
 * writes them; the database enforces that, because a waiter editing their own
 * row would be granting themselves the permission.
 */
export type PermKey =
  | 'order'
  | 'moveTable'
  | 'editPrice'
  | 'track'
  | 'editTicket'
  // Müdür izinleri (3 Kas 2026): patron kişi kişi açıyor.
  | 'menu'
  | 'reports'
  | 'staff'
  | 'settings'

/** Müdüre verilebilen izinler; patron her zaman hepsine sahip. */
export type ManagerPerm = 'menu' | 'reports' | 'staff' | 'settings'

export interface PermSpec {
  key: PermKey
  name: string
  description: string
  /** The roles the permission means anything for. */
  roles: Role[]
  /**
   * Kurumun bu modülü yoksa yetki hiç gösterilmiyor: modülü sistem yöneticisi
   * veriyor, patron yalnızca kimde açık olacağını seçiyor.
   */
  feature?: FeatureKey
  /**
   * Hiç ayarlanmamışken açık sayılır (yok = açık). Sipariş alma böyle: eklenmeden
   * önce açılmış garsonlar sipariş almayı kaybetmesin.
   */
  defaultOn?: boolean
}

export const PERMS: PermSpec[] = [
  {
    key: 'order',
    name: 'Sipariş alma',
    description: 'Menüden ürün ekleyip masaya kaydedebilir. Kapalıyken yalnız masaları ve takibi görür.',
    // Kullanıcı, 20 Eki 2026: garsonun sipariş almasını açıp kapatmak.
    roles: ['waiter'],
    defaultOn: true,
  },
  {
    key: 'moveTable',
    name: 'Masa taşıma',
    description: 'Açık bir adisyonu başka masaya taşıyabilir ve masaları birleştirebilir.',
    // Cashiers and owners may always move a ticket: correcting a bill written
    // on the wrong table is the counter's daily work, and putting that behind
    // a switch leaves a bill nobody can fix.
    roles: ['waiter'],
  },
  {
    // Kullanıcı, 29 Eki 2026: masaya fazla ya da eksik girilen ürünü garson
    // düzeltebilsin. Kasa ve patron her zaman düzeltebiliyor.
    key: 'editTicket',
    name: 'Adisyon düzenleme',
    description:
      'Fişi basılmış ürünü azaltıp çıkarabilir, başka garsonun girdiği ürünü düzeltebilir. Kapalıyken yalnız kendi eklediğini, fişi basılmadan değiştirir.',
    roles: ['waiter'],
  },
  // Fiyat değiştirme listeden kalktı (25 Eki 2026, kullanıcı); daha önce
  // verilmiş yetki (perms.editPrice) aynen çalışıyor.
  {
    key: 'track',
    name: 'Sipariş takip',
    description: 'Girdiği ürünlerden masaya götürülmeyenleri listeler; 10 ve 20 dakikada hatırlatır.',
    roles: ['waiter'],
    feature: 'takip',
  },
  // Müdür (3 Kas 2026, kullanıcı): kasiyerin yaptıklarını hep yapar; bunları
  // patron seçer. Hesabım/abonelik, kurumu silme ve patron hesabı hiç yok.
  {
    key: 'menu',
    name: 'Menü düzenleme',
    description: 'Ürün, kategori, fiyat, kampanya ve stok.',
    roles: ['manager'],
  },
  {
    key: 'reports',
    name: 'Raporlar',
    description: 'Gün sonu, dönem raporu ve gelir-gider.',
    roles: ['manager'],
  },
  {
    key: 'staff',
    name: 'Personel yönetimi',
    description: 'Kasiyer, garson ve mutfak hesaplarını ekler, düzenler, kaldırır. Patron ve müdürlere dokunamaz.',
    roles: ['manager'],
  },
  {
    key: 'settings',
    name: 'Masa ve ayarlar',
    description: 'Masa ve salon düzeni, fiş, yazıcılar, mutfak ekranları, QR menü.',
    roles: ['manager'],
  },
]

export type Perms = Partial<Record<PermKey, boolean>> & {
  /**
   * Mutfak personelinin tezgahı (Mutfak ekranı ilk açılışta bunu gösterir).
   * Boş ya da yok: bütün tezgahlar.
   */
  station?: string
  /**
   * Mutfak ekranının (cihaz) tezgahları. Boş ya da yok: bütün tezgahlar.
   * Ekran bunların dışını göstermez, tablette değiştirilemez.
   */
  stations?: string[]
  /**
   * Garsonun baktığı salonlar (28 Eki 2026). Boş ya da yok: bütün salonlar.
   * Garson yalnız bunların masalarını görür ve yalnız bunlara taşır.
   */
  halls?: string[]
}

export interface AdminUser {
  uid: string
  email: string
  /** Login name ("kasa"), derived from the synthetic email. */
  username: string
  displayName?: string
  role: Role
  /** Per-person permissions. Absent means none granted. */
  perms?: Perms
}

/**
 * What an institution is. Every institution is a restaurant or cafe today;
 * what differs between them is turned on and off through `features`.
 */
export type TenantType = 'restaurant'

/**
 * The optional parts of the application, per institution.
 *
 * Off unless written: an institution that has never been given a flag sees
 * exactly what it saw before the flag existed. Only the system administrator
 * can change these, a database trigger enforces it, because an owner reaching
 * their own settings screen must not be able to switch on something they have
 * not been given (supabase/activation.sql).
 */
export type FeatureKey =
  | 'cari'
  | 'qr'
  | 'sadaka'
  | 'takip'
  | 'tarti'
  | 'gorsel'
  | 'yemek'
  // Paketlerle (29 Eki 2026, plans.ts) anahtara bağlananlar; önceden herkese açıktı.
  | 'mutfak'
  | 'stok'
  | 'maliyet'
  // Ek modül (30 Eki 2026): tezgahta müşterinin kendi sipariş verdiği tablet.
  | 'kiosk'
  // Ek modül (30 Eki 2026): müşteri sadakat, damga kartı ('sadaka' bahşiş, ayrı).
  | 'damga'
  // Masadan QR ile sipariş, garson çağırma, hesap isteme (30 Eki 2026, ek modül).
  | 'qrsiparis'

export interface FeatureSpec {
  key: FeatureKey
  name: string
  description: string
}

/**
 * Declared here rather than typed into the panel as free text: a mistyped key
 * in a text box is a feature that silently stays off, with nothing on screen
 * to say why.
 */
export const FEATURES: FeatureSpec[] = [
  {
    key: 'mutfak',
    name: 'Mutfak ekranı',
    description: 'Tezgah siparişlerini gösteren tabletler ve mutfak personeli hesabı.',
  },
  {
    key: 'stok',
    name: 'Stok takibi',
    description: 'Stoklu ürün: satıldıkça düşer, bitince Tükendi olur.',
  },
  {
    key: 'maliyet',
    name: 'Maliyet ve hammadde',
    description: 'Ürün maliyeti, hammaddeler ve kar oranı; yalnız patron görür.',
  },
  {
    key: 'damga',
    name: 'Müşteri sadakat',
    description: 'Damga kartı: müşteri kodunu kasada söyler, her ödemede damga birikir; kart dolunca indirim uygulanır.',
  },
  {
    key: 'kiosk',
    name: 'Kiosk',
    description: 'Müşteri siparişini tezgahtaki tabletten kendisi verir; sipariş kasada ödenince mutfağa düşer.',
  },
  {
    key: 'cari',
    name: 'Cari hesap',
    description: 'Kişilere borç yazma ve tahsilat takibi.',
  },
  {
    key: 'qr',
    name: 'QR menü',
    description:
      'Müşteri karekodu okutup menüyü telefonundan görür. Tükenen ürün anında tükenmiş görünür.',
  },
  {
    key: 'qrsiparis',
    name: 'QR Sipariş',
    description: 'Müşteri masadaki karekoddan sipariş verir, garson çağırır, hesap ister. QR Menü de açık olmalı.',
  },
  {
    key: 'sadaka',
    name: 'Üstü kalsın',
    description:
      'Adisyondan fazla verilen para bahşiş olarak ayrı bir kalem kaydedilir.',
  },
  {
    key: 'takip',
    name: 'Sipariş takip',
    description:
      'Garson aldığı siparişlerden masaya götürülmeyenleri görür, götürdükçe işaretler. ' +
      '10 ve 20 dakikayı geçen ürün için uyarı verir.',
  },
  {
    key: 'tarti',
    name: 'Tartılı ürünler',
    description:
      'Kiloyla satılan ürünler (börek, tatlı): kap darası düşülerek porsiyon porsiyon tartılır. ' +
      'Masada alınan sipariş tezgahta tartılana kadar ödeme alınamaz.',
  },
  {
    key: 'gorsel',
    name: 'Görselli menü',
    description:
      'Ürünlere fotoğraf eklenir; QR menüde ürünün yanında küçük fotoğraf, dokununca büyüğü açılır. ' +
      'Kasa ve garson ekranı değişmez.',
  },
  {
    key: 'yemek',
    name: 'Yemek kartı',
    description:
      'Kasada ve parçalı ödemede Yemek Kartı ile ödeme: Ticket, Sodexo, Multinet, Setcard, Metropol, Tokenflex. ' +
      'Raporda ayrı satır olarak görünür.',
  },
]

export type Features = Partial<Record<FeatureKey, boolean>>

export interface Tenant {
  id: string
  name: string
  displayName: string
  currency: 'TRY'
  timezone: string
  type?: TenantType
  /** Which optional parts are switched on. Absent means none. */
  features?: Features
  /** Percent off for the "İndirimli" payment, 0-100. */
  studentDiscountPercent?: number
  /** Discount rates offered at the till; two by default. */
  discounts?: number[]
  /** What each role is called in this yurt; editable, not baked into the UI. */
  roleLabels?: Partial<Record<Role, string>>
  /** What the stall slip shows; see ReceiptSettings. */
  receipt?: ReceiptSettings
  /** What the till's own slip shows when payment is taken. */
  cashReceipt?: ReceiptSettings
}

/**
 * What the printed slip shows. Every field is optional and every default is
 * "as it was before this existed", so a yurt that never opens the settings
 * keeps the receipt it already had.
 */
/**
 * Ağ (Ethernet) fiş yazıcısı, Ayarlar > Yazıcılar (20 Eki 2026). Kuruma ait:
 * bütün cihazlar aynı listeyi görür; hangi bilgisayarın basacağı cihaz
 * ayarı (services/printBridge). Fiş kasa bilgisayarındaki Yazıcı Köprüsü
 * üzerinden IP:port'a ESC/POS olarak gider.
 */
export interface NetworkPrinter {
  id: string
  name: string
  /** Yazıcının IP adresi (modemde sabitlenmiş), örn. 192.168.1.50. */
  host: string
  /** Neredeyse her fiş yazıcısında 9100. */
  port: number
  /** Kağıt genişliği, mm. */
  paper: 80 | 58
  /** Kasa fişini (ödeme) basar. */
  cash: boolean
  /** Bu tezgahların fişlerini basar. */
  stations: string[]
  /** Hiçbir yazıcıya verilmemiş tezgahların fişlerini de basar. */
  others: boolean
}


/**
 * Bütün fişlerde işletme adının altındaki satır (28 Eki 2026, kullanıcı):
 * Adissoo'nun reklamı. İşletme değiştiremez; eski "Alt başlık" ayarı kalktı.
 */
export const RECEIPT_BRAND = 'Adissoo, adissoo.com'

export interface ReceiptSettings {
  /** Printed largest, at the top. Blank falls back to the yurt's name. */
  title?: string
  /**
   * Eski alt başlık. 28 Eki 2026'dan beri okunmuyor: o satırda her fişte
   * RECEIPT_BRAND yazıyor, ayarlardan kaldırıldı. Eski kayıtlarda duruyor.
   */
  subtitle?: string
  /** Last line of the slip. */
  footer?: string
  showReceiptNo?: boolean
  showDate?: boolean
  showTable?: boolean
  showWaiter?: boolean
  /**
   * Line spacing and type size, as percentages of the printed defaults.
   *
   * These are what a roll of paper actually gets spent on: a long ticket at
   * 100% can outrun the roll, and dropping to 80% fits a third more on the same
   * length without making the slip unreadable at a counter.
   */
  linePercent?: number
  fontPercent?: number
  /** Blank paper fed past the tear bar, in millimetres. */
  feedMm?: number
  /**
   * The rules that divide the slip into sections.
   *
   * Thermal paper is unforgiving here: a hairline can come out grey or drop
   * out altogether on a worn head, and a dashed rule at 1px prints as a row of
   * specks. Solid and thick is the safe default; the rest is here because
   * printers differ and the only way to know is to run one.
   */
  ruleWidthPx?: number
  ruleStyle?: 'solid' | 'dashed' | 'dotted' | 'double'
  /**
   * A box around the station name at the top of the slip.
   *
   * On a counter with slips from three stations landing in the same tray, the
   * name is what sorts them, and a boxed word is found at a glance in a way a
   * larger one is not.
   */
  boxStation?: boolean
  /**
   * The summary line above the footer ("Pide Fırını, 1 ürün").
   *
   * The station name is already the largest thing at the top of the slip, so
   * on a single-station restaurant this line repeats what is plainly visible and
   * spends paper saying it.
   */
  showStationTotal?: boolean
  /** The station name printed again at the foot, under the last rule. */
  showFooterStation?: boolean
  /**
   * Prices. Off for a stall slip, payment was taken at the till, and an
   * amount in a customer's hand at a counter invites being charged twice.
   */
  showPrices?: boolean
  /**
   * Kasa fişi tezgahlara bölünsün mü (16 Eki 2026). Panelde seçeneği yok
   * (kullanıcı: "zaten böyle çalışıyor"), kasa fişi her zaman bölünerek
   * basılıyor; alan yalnız aşağıdaki işaret için kaydediliyor.
   *
   * Kasa fişinde bu alanın hiç olmaması "patron yeni panelden kasa fişini
   * kaydetmedi" demek: o zaman ödeme fişi eski davranışla, garson fişinin
   * ayarlarıyla basılıyor. Emirler'de eski sekmeden kalma, hiç basılmamış bir
   * cash_receipt var; sessizce devreye girmesin diye.
   */
  splitByStation?: boolean
  /**
   * Fişi basılmayan tezgahlar (25 Eki 2026). Bazı tezgahlar siparişi yalnız
   * mutfak ekranından takip ediyor, kağıda gerek yok. Sipariş ve "Masa
   * değişti" fişlerinde bu tezgahların bölümü hiç çizilmiyor, kasa fişine
   * dokunulmuyor. Ayarlar > Yazıcılar'dan; yalnız garson fişinin ayarında.
   */
  skipStations?: string[]
}

/**
 * The slip handed to a stall: what to prepare, nothing else. This is what the
 * defaults have always described, so they stay exactly as they were.
 */
export const DEFAULT_RECEIPT: Required<Omit<ReceiptSettings, 'title' | 'skipStations' | 'subtitle'>> = {
  footer: 'Teşekkür ederiz',
  showReceiptNo: true,
  showDate: true,
  showTable: true,
  showWaiter: true,
  linePercent: 100,
  fontPercent: 100,
  feedMm: 12,
  // Matches what the slips have always printed.
  ruleWidthPx: 2,
  ruleStyle: 'solid',
  boxStation: false,
  showStationTotal: true,
  showFooterStation: true,
  showPrices: false,
  splitByStation: true,
}

/**
 * The slip kept at the till when payment is taken. It answers a different
 * question from the stall slip, what was charged, not what to cook, so it
 * carries prices and drops the per-station repetition.
 */
export const DEFAULT_CASH_RECEIPT: Required<Omit<ReceiptSettings, 'title' | 'skipStations' | 'subtitle'>> = {
  footer: 'Teşekkür ederiz',
  showReceiptNo: true,
  showDate: true,
  showTable: true,
  showWaiter: true,
  linePercent: 100,
  fontPercent: 100,
  feedMm: 12,
  // Matches what the slips have always printed.
  ruleWidthPx: 2,
  ruleStyle: 'solid',
  boxStation: false,
  showStationTotal: false,
  showFooterStation: false,
  showPrices: true,
  splitByStation: false,
}

/**
 * The discount rates offered at the till, in percent. Two fixed rates rather
 * than one: a restaurant gives different reductions to different groups, and
 * picking a figure by hand at the counter is where mistakes get made.
 */
export const DEFAULT_DISCOUNTS = [20, 30]

export const DEFAULT_STUDENT_DISCOUNT = 50

/**
 * What each role is called on screen.
 *
 * Fixed rather than configurable. Institutions used to be able to rename these
 *, one called its cashier "Muhasebe", but a name that means something to one
 * of them means nothing to anyone helping, and a screenshot from a counter
 * became impossible to read at a distance. The role a person holds is a
 * property of the system, so it reads the same everywhere.
 */
export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Patron',
  manager: 'Müdür',
  cashier: 'Kasiyer',
  waiter: 'Garson',
  kitchen: 'Mutfak',
  kiosk: 'Kiosk',
}

/** @deprecated Kept while the old name is phased out; same object. */
export const DEFAULT_ROLE_LABELS = ROLE_LABELS

export interface Category {
  id: string
  name: string
  sortOrder: number
  active: boolean
  /**
   * Which counter prepares this category. Orders print one slip per station,
   * so categories sharing a station share a slip. Blank means the default
   * station, a yurt that doesn't split its counters needs no setup.
   */
  station?: string
  /**
   * Default note options for items in this category, so a menu is set up once
   * per section rather than once per item. A product may override it.
   */
  noteOptions?: string[] | null
  /**
   * Paletteki rengin kimliği (utils/categoryColors). Boşsa sırasına göre
   * varsayılan renk.
   */
  color?: string | null
}

export const DEFAULT_STATION = 'Mutfak'

/**
 * Offered where nothing has been configured yet: nothing. A kebab shop's five
 * (Soğansız, Domatessiz…) arrived on every new restaurant, a café and a fish
 * house too, and nobody knew where they came from. The owner now adds the
 * notes their kitchen uses; see NoteOptionsDialog.
 */
export const DEFAULT_NOTE_OPTIONS: string[] = []

export interface Product {
  id: string
  name: string
  categoryId: string
  /** Integer kuruş, 150 TL is stored as 15000. Never a float. */
  priceKurus: number
  description?: string | null
  active: boolean
  /**
   * Out of stock at the stall. Distinct from `active`, which takes an item off
   * the menu entirely: a sold-out item stays on the grid, marked, and can still
   * be rung up, a stall often finds one more portion, and the cashier is the
   * one talking to the customer. This only says "stop offering it".
   */
  soldOut?: boolean
  sortOrder: number
  /**
   * The note options offered for this item. Asking whether a bottle of water
   * should come without onion is nonsense, so this is per product rather than
   * one list for the whole menu.
   *
   * Undefined means "inherit the category's list", which is what keeps setting
   * this up from being 33 separate decisions. An empty array means the opposite:
   * this item takes no notes at all.
   */
  noteOptions?: string[] | null
  /**
   * Kiloyla satılıyor (Tartılı ürünler modülü). priceKurus o zaman 1 kg'ın
   * fiyatı; satır tutarı tartımdan çıkıyor.
   */
  byWeight?: boolean
  /**
   * Ürün fotoğrafının herkese açık adresi (Görselli menü modülü). Yalnız QR
   * menüde görünüyor.
   */
  imageUrl?: string | null
  /** Tartıldığı kap ve darası; tartım ekranında hazır seçili gelir. */
  tare?: Tare | null
  /**
   * Stok (16 Eki 2026, supabase/stok.sql). Boş (null) = sınırsız, bugünkü gibi.
   * Doluysa satışla veritabanında kendiliğinden düşüyor; 0'a inince ürün
   * Tükendi oluyor. Kiloyla satılan üründe kg.
   */
  stock?: number | null
  /** "Az kaldı" eşiği: stok bunun altına inince menüde ve kasada uyarı. */
  stockMin?: number | null
  /**
   * Barkodlar (16 Eki 2026, supabase/barkod.sql). Kasada USB okuyucuyla
   * okutulunca ürün sepete düşüyor. Birden çok olabilir (farklı tedarikçi).
   */
  barcodes?: string[]
  /**
   * Ürüne özel tezgah (20 Eki 2026, supabase/urun-tezgah.sql). Boş = kategorinin
   * tezgahı. Kategorisinden ayrı yerde hazırlanan tek ürün için.
   */
  station?: string | null
  /**
   * Porsiyonlar (3 Kas 2026, supabase/porsiyon.sql): "1,5 Porsiyon" gibi,
   * her biri kendi fiyatıyla. priceKurus 1 porsiyon. Boşsa ürüne basınca
   * doğrudan eklenir; doluysa porsiyon seçtirilir.
   */
  portions?: Portion[] | null
}

export interface Portion {
  name: string
  priceKurus: number
}

/** Porsiyon seçiminde ürünün kendi fiyatının adı. */
export const BASE_PORTION = '1 Porsiyon'

/** Bir kap ve boşken ağırlığı (dara), gram. */
export interface Tare {
  name: string
  gr: number
}

/** Tartılı satırın bir tabağı. */
export interface WeighPlate {
  /** Terazide okunan, kapla birlikte. */
  grossGr: number
  /** Kap düşülmüş. */
  netGr: number
  kurus: number
}

/**
 * Tartılı satırın tartım bilgisi.
 *
 * `id` satırın kimliğinin parçası: aynı ürün iki ayrı tartımla iki satır
 * olur. Masada "bekliyor" diye giren satır tezgahta tartılınca id aynı
 * kalıyor; mutfağa ikinci kez fiş gitmiyor.
 */
export interface LineWeigh {
  id: string
  status: 'pending' | 'done'
  /** Kilo fiyatı; unitPriceKurus da bu. */
  perKgKurus: number
  tare?: Tare
  plates?: WeighPlate[]
  /**
   * Tartımı yapan kişinin adı (11 Eki 2026). Gelişmiş Rapor'da tezgahtar
   * bazlı gramaj sapması bundan çıkıyor; eski tartımlarda yok.
   */
  weighedBy?: string
}

/**
 * Someone the till keeps an account for.
 *
 * Never deleted once they have any history, only deactivated: an entry whose
 * person had been removed would answer "how much" without answering "from
 * whom", which is the one question a ledger exists to answer.
 */
export interface Debtor {
  id: string
  name: string
  phone?: string
  note?: string
  active: boolean
  /**
   * "Borcu yapılandır"da bu kişi için sabit tutulan oran (yüzde). Ekran
   * açılınca seçili gelir; yoksa undefined.
   */
  discountPercent?: number
}

/**
 * One movement on an account: a debt taken on, or a payment made against it.
 *
 * The direction lives in `kind` rather than in the sign of the amount. A
 * field that accepts a negative figure invites the question of what a
 * negative payment means, and there is no useful answer.
 */
export interface DebtorEntry {
  id: string
  debtorId: string
  /**
   * 'discount': tahsilatta uygulanan indirim. Bakiyeyi ödeme gibi düşürür
   * ama kasaya para girişi değil; geçmişte ayrı satır olarak görünür.
   */
  kind: 'debt' | 'payment' | 'discount'
  /** Always positive. `kind` says which way it moves the balance. */
  amountKurus: number
  /** The sale this debt came from, when it came from one. */
  orderId?: string
  description?: string
  dayKey: string
  createdAtLocal: number
  createdByName?: string
}

/**
 * A person and what they owe, as the screens need it.
 *
 * `balanceKurus` is summed from entries and stored nowhere. A stored balance
 * drifts from the entries it claims to summarise, a write half-lands, a
 * correction misses it, and once the two disagree there is no way to tell
 * which one is lying.
 */
export interface DebtorBalance extends Debtor {
  balanceKurus: number
  /** When this person last had any movement, for ordering the list. */
  lastMovedAt: number
}

/** A place an order can be opened against. Entered by the owner up front. */
export interface Table {
  id: string
  name: string
  sortOrder: number
  active: boolean
  /**
   * Which section the table stands in, or undefined for one that stands on
   * its own. Optional on purpose: every table opened before sections existed
   * has no section and keeps working, the settings screen gathers those
   * under "Salonsuz" rather than making anyone sort them first.
   */
  hallId?: string
  /**
   * Masanın karekod anahtarı (QR Sipariş, supabase/qr-siparis.sql). Müşteri
   * bu anahtarla masadan sipariş verir; patron yenileyebilir.
   */
  qrToken?: string
}

/**
 * A section of the room, "Bahçe", "Üst Kat", "Salon".
 *
 * A flat list of tables stops reading as a list somewhere around twenty, and
 * "Bahçe 3" and "Salon 3" are different tables a waiter tells apart by which
 * part of the building they are in. Its own record rather than a word on each
 * table: renaming the section then follows every table in it.
 */
/** Ayarlar > Kampanyalar'da kurulan kural. Hesabı utils/campaigns.ts yapıyor. */
export type CampaignKind = 'percent' | 'amount' | 'bogo'

export interface Campaign {
  id: string
  name: string
  kind: CampaignKind
  /** percent: eşleşen ürünlerde yüzde. */
  percent?: number
  /** amount: düşülecek tutar. */
  amountKurus?: number
  /** amount: eşleşen ürünlerin toplamı en az bu kadar olmalı. */
  minTotalKurus?: number
  /** bogo: "3 al 2 öde" için buyQty 3, payQty 2. */
  buyQty?: number
  payQty?: number
  /** İkisi de boşsa bütün menü. */
  categoryIds: string[]
  productIds: string[]
  /** 0 Pazar ... 6 Cumartesi; boşsa her gün. */
  days: number[]
  /** "14:00"; ikisi de boşsa bütün gün. */
  startTime?: string
  endTime?: string
  /** "2026-10-14"; boşsa sınırsız. */
  startsOn?: string
  endsOn?: string
  active: boolean
  sortOrder: number
  /** QR menüde pencereyle gösterilsin mi (personel indirimi gibileri kasada kalsın). */
  showOnQr: boolean
  /** Patronun yüklediği afiş; yoksa QR menü stok fotoğraf koyuyor. */
  imageUrl?: string | null
}

/** Adisyona uygulanan kampanya, uygulandığı anki adı ve tutarıyla. */
export interface OrderCampaign {
  id: string
  name: string
  kurus: number
}

export interface Hall {
  id: string
  name: string
  sortOrder: number
}

/** Hammaddenin fiyat birimi. */
export type MaterialUnit = 'kg' | 'adet' | 'lt'

/** Hammadde (18 Eki 2026): ortak liste, yalnız patron görür. */
export interface Material {
  id: string
  name: string
  unit: MaterialUnit
  /** Birim (1 kg, 1 adet, 1 lt) fiyatı, kuruş. */
  priceKurus: number
}

/** Reçete satırı: 1 kg ya da 1 adet ürün için hammaddenin biriminde miktar. */
export interface RecipeLine {
  materialId: string
  qty: number
}

/**
 * Ürün maliyeti (18 Eki 2026), yalnız patron görür. direct: costKurus;
 * recipe: hammaddelerden hesap. Kiloyla satılan üründe 1 kg'ın, diğerlerinde
 * 1 adedin maliyeti.
 */
export interface ProductCost {
  productId: string
  mode: 'direct' | 'recipe'
  costKurus: number | null
  recipe: RecipeLine[]
}

/**
 * `paid` is what a till actually records: the question at the counter is
 * whether the money was taken, not what it was taken in. Cash and card are kept
 * so past days keep reading correctly and a yurt that wants the split back has
 * somewhere to put it.
 */
export type PaymentMethod = 'paid' | 'cash' | 'card' | 'credit' | 'student' | 'split' | 'meal'

export const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  paid: 'Ödendi',
  cash: 'Nakit',
  card: 'Kredi Kartı',
  credit: 'Borç',
  student: 'İndirimli',
  split: 'Kısmi Ödeme',
  meal: 'Yemek Kartı',
}

/**
 * Yemek kartı markaları (21 Eki 2026, supabase/yemek-karti.sql). Sabit liste
 * (kullanıcı); veritabanındaki kural da aynı adları kabul ediyor, ikisi
 * birlikte değişir.
 */
export const MEAL_CARDS = ['Ticket', 'Sodexo', 'Multinet', 'Setcard', 'Metropol', 'Tokenflex'] as const
export type MealCard = (typeof MEAL_CARDS)[number]

/**
 * Set only when paymentMethod is 'split', a customer paying part cash and
 * part card. The day report sums these instead of the order total, so a
 * mixed payment still lands in the right column.
 */
export interface PaymentSplit {
  cashKurus: number
  cardKurus: number
}

/**
 * Name and unit price are copied from the product at sale time. If the menu
 * price changes mid-service, historical orders and reprints must keep showing
 * what the customer actually paid.
 */
export interface OrderLine {
  productId: string
  name: string
  unitPriceKurus: number
  qty: number
  lineTotalKurus: number
  /** Preparation note for this line, "soğansız", "acısız". */
  note?: string
  /**
   * Counter this line goes to, copied from its category when rung up. Stored
   * on the line so a later category change can't reroute a printed slip.
   */
  station?: string
  /**
   * Who rang this line up.
   *
   * A table opened by one waiter is often added to by another, someone walks
   * past, the customer asks, they take it. Afterwards nobody can say which
   * round was whose, and a mistyped item has no owner to ask about it. Stored
   * on the line rather than the ticket because it differs line by line.
   */
  byName?: string
  /**
   * How many of `qty` have been taken to the table (Sipariş Takip). Absent on
   * lines from before the feature: those count as fully served, so opening
   * the panel for the first time does not fill it with old rounds.
   */
  servedQty?: number
  /** When the oldest unserved unit was rung in (ms); absent when none waits. */
  waitingSince?: number
  /**
   * Tartılı ürün. Bekliyorken tutar 0 ve ödeme kilitli; tartılınca tutar
   * tabakların toplamı, qty tabak sayısı.
   */
  weigh?: LineWeigh
  /** Seçilen porsiyonun adı ("1,5 Porsiyon"); ürünün kendi fiyatıysa yok. */
  portion?: string
}

/**
 * 'open' is a waiter's ticket waiting to be settled at the till. It becomes
 * 'completed' the moment a cashier takes payment, that is also when the
 * receipt number is issued, so open tickets never consume one.
 */
export type OrderStatus = 'open' | 'completed' | 'void'

export interface Order {
  id: string
  /** Empty while the ticket is still open; issued when payment is taken. */
  receiptNo: string
  /** 'YYYY-MM-DD' business day. Equality-queryable offline, unlike createdAt. */
  dayKey: string
  deviceId: string
  /** Resolves on the server; null while a write is still pending offline. */
  createdAt: number | null
  /** Always populated locally, so offline orders can still be sorted. */
  createdAtLocal: number
  /** Device clock when a round was last added, used to measure arrival lag. */
  updatedAtLocal?: number
  /** Tahsil edildiği an (ms, sunucu saati); eski kayıtlarda yok. */
  settledAt?: number
  /**
   * Set when the last round was saved deliberately without printing.
   *
   * The machine with the printer is usually not the one the round was rung in
   * on, and it cannot tell a silent save from a waiter's order, the document
   * changes the same way either way. This is how it is told.
   */
  silent?: boolean
  cashierUid: string
  cashierEmail: string
  /** Table name copied at the time, deleting a table won't orphan tickets. */
  tableName?: string
  /** Who opened the ticket; shown faintly at the till. */
  openedByName?: string
  /**
   * Everyone who has added to this ticket, in the order they first did, the
   * opener included. Kept alongside the per-line names so the ticket can list
   * them without walking every line.
   */
  byNames?: string[]
  openedByUid?: string
  lines: OrderLine[]
  itemCount: number
  /** What was actually charged, after any discount. */
  totalKurus: number
  /** Pre-discount total; only set when a discount was applied. */
  subtotalKurus?: number
  discountKurus?: number
  discountPercent?: number
  /** Kasiyerin eklediği kampanyalar; tutarları subtotalKurus'tan düşülüyor. */
  campaigns?: OrderCampaign[]
  /** Absent while the ticket is open, nothing has been paid yet. */
  paymentMethod?: PaymentMethod
  /** Present when paymentMethod is 'split'. */
  split?: PaymentSplit
  cashReceivedKurus?: number
  changeKurus?: number
  /**
   * Money left on top of the bill as a donation, the customer who pays 7000
   * on a 6000 ticket and says keep it.
   *
   * Not revenue and not change: no product stands behind it and none of it
   * goes back. The drawer holds 7000 while the day's takings are 6000, and a
   * report that cannot separate the two reconciles against neither.
   */
  bahsisKurus?: number
  /**
   * How the donation itself was handed over. Asked separately from the bill:
   * paying the ticket by card and leaving the donation in cash is common
   * enough that one method for both would record it wrong.
   */
  bahsisPaymentMethod?: 'cash' | 'card'
  /** paymentMethod 'meal' iken hangi yemek kartı. */
  mealCard?: MealCard
  /** Günün Gel-Al sırası (#1, #2…), yalnız masasız satışta (supabase/gelal-no.sql). */
  takeawayNo?: number
  /**
   * Kiosk siparişi kasada ödenmeyi bekliyor (30 Eki 2026, supabase/kiosk.sql):
   * ödenene kadar mutfağa gitmez, kasada "Kiosk" şeridinde görünür.
   */
  awaitingPayment?: boolean
  /** Siparişin geldiği yer; şimdilik yalnız 'kiosk'. */
  source?: 'kiosk'
  /**
   * Kiosk takibi (31 Eki 2026, supabase/kiosk-takip.sql): müşterinin
   * telefon sayfasının gizli anahtarı ve kasada/teslimde sorulan 4 haneli kod.
   */
  trackToken?: string
  pickupCode?: string
  /** Yazıcısız cihazda yapıldı: fişlerini otomatik yazdırması açık kasa basacak. */
  printAtTill?: boolean
  note?: string
  status: OrderStatus
  voidedAt?: number | null
  voidedBy?: string
  /** Not persisted, mirrors snapshot metadata so the UI can show sync state. */
  pending?: boolean
  /**
   * Tutarla ara ödeme (22 Eki 2026). Açık adisyonda: o ana kadar alınan ara
   * ödemelerin toplamı, kalan = totalKurus - prepaidKurus. Kapanan satışta:
   * kapanıştan önce alınanlar; totalKurus o zaman yalnız kalan tutar.
   */
  prepaidKurus?: number
  /**
   * Masa notu (4 Kas 2026): açık adisyona yazılan kısa not, "doğum günü,
   * pasta 21:00'de" gibi. Adisyonla kapanıyor; kasa ve garson yazabiliyor.
   */
  tableNote?: string
  /** Ara ödeme kaydında: alındığı adisyonun kimliği (ürünsüz satış). */
  partialOf?: string
  /** Birleştirmede iptal edilen kaynağın hedef adisyonu (23 Eki 2026). */
  mergedInto?: string
  /**
   * Yalnız basılacak "MASA DEĞİŞTİ" fişinde (ReceiptKind 'move'): nereden
   * geldiği, kimin taşıdığı, ne zaman. Kayıtta yok.
   */
  move?: { from: string; by?: string; at: number }
  /** Yalnız "Son fişler"den tekrar basılan fişte: başında "TEKRAR BASIM". */
  reprint?: boolean
}

export type NewOrder = Omit<Order, 'id' | 'createdAt' | 'createdAtLocal' | 'pending'>

/** Mutfak ekranı: bir fişteki ürün. */
export interface KitchenItem {
  /** Sipariş satırının kimliği (ürün|not|giren|tartım); garsonun "hazır" eşleşmesi. */
  key: string
  name: string
  qty: number
  note?: string | null
  status: 'new' | 'cooking' | 'ready'
  /** ms */
  startedAt?: number
  readyAt?: number
}

/**
 * Mutfak ekranı fişi: siparişin bir tezgaha giden yeni ürünleri. Sipariş
 * kaydedilince veritabanı tetiği yazıyor (supabase/mutfak-ekrani.sql).
 */
export interface KitchenTicket {
  id: string
  orderId: string
  station: string
  /** Masa adı ya da "Gel-Al #12". */
  label: string
  byName: string
  items: KitchenItem[]
  /** open: mutfakta; done: gönderildi; void: sipariş iptal. */
  status: 'open' | 'done' | 'void'
  createdAt: number
  doneAt?: number
  /** Masa taşıma/birleştirme (23 Eki 2026): önceki masa, taşıyan, ne zaman (ms). */
  movedFrom?: string
  movedBy?: string
  movedAt?: number
  /** Kiosk siparişi müşteriye teslim edildi (1 Kas 2026, supabase/kiosk-teslim.sql). */
  handedAt?: number
}

/**
 * Müşteri sadakat kuralı (tenants.loyalty, supabase/sadakat.sql). Patron
 * Ayarlar > Sadakat'tan yazar; damgayı sunucu bu kuralla sayar.
 */
export interface LoyaltySettings {
  /** Her ödemede 1 damga ya da tutar başına. */
  per: 'visit' | 'amount'
  /** per = amount: bu kadar kuruşa 1 damga. */
  amountKurus: number
  /** Kart kaç damgada dolar. */
  target: number
  /** Kart dolunca hesaba uygulanan indirim, yüzde. */
  rewardPercent: number
}

export const DEFAULT_LOYALTY: LoyaltySettings = { per: 'visit', amountKurus: 10000, target: 10, rewardPercent: 20 }

/** Müşterinin sadakat kartı (kasada kodla bulunur). */
export interface LoyaltyCard {
  id: string
  code: string
  token: string
  name: string
  phone: string | null
  stamps: number
  rewardsUsed: number
}
